import React from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Controller, useForm } from 'react-hook-form'
import useApi from '../../../common/utils/api/useApi'
import { OrgMembershipApiClient } from '../OrgMembershipSlice'
import { useAppSelector } from '../../../app/typedReduxHooks'
import { RootState } from '../../../app/store'
import { AxiosError, AxiosResponse } from 'axios'
import { AdminRole } from '../../users/type/AdminRole'
import { convertRoleToPermissions } from '../../users/utils/AdminPermissions'

interface InviteFormInputs {
  email: string
  isPrescriber: boolean
  adminRole: AdminRole | null
}

interface InviteModalProps {
  isOpen: boolean
  onClose: () => void
  noEnoughSeats: boolean
}

interface PrescriberRoleProps {
  isChecked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
  noEnoughSeats: boolean
}

interface AdminRoleProps {
  role: AdminRole
  label: string
  description: string
  isSelected: boolean
  onChange: (role: AdminRole) => void
}

const PrescriberRole: React.FC<PrescriberRoleProps> = ({
  isChecked,
  disabled,
  onChange,
  noEnoughSeats,
}) => (
  <Box>
    <HStack spacing={2}>
      <Checkbox
        isChecked={isChecked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      >
        Prescriber
      </Checkbox>
      <Badge fontSize="xs" colorScheme="purple" variant="subtle">
        billable
      </Badge>
    </HStack>
    {noEnoughSeats && (
      <Text fontSize="sm" color="purple" ml={6}>
        More seats required for new prescribers.
      </Text>
    )}
    <Text fontSize="sm" color="gray.600" ml={6}>
      This role allows the user to create prescriptions and manage patient health data.
    </Text>
  </Box>
)

const AdminRoleOption: React.FC<AdminRoleProps> = ({
  role,
  label,
  description,
  isSelected,
  onChange,
}) => (
  <Box>
    <Checkbox isChecked={isSelected} onChange={() => onChange(role)}>
      {label}
    </Checkbox>
    <Text fontSize="sm" color="gray.600" ml={6}>
      {description}
    </Text>
  </Box>
)

const ADMIN_ROLES = [
  {
    role: 'patient_admin' as AdminRole,
    label: 'Patient Admin',
    description: 'This role allows the user to manage patients.',
  },
  {
    role: 'rx_admin' as AdminRole,
    label: 'Rx Admin',
    description: 'This role allows the user to view prescriptions and manage patients.',
  },
]

export const InviteUserModal: React.FC<InviteModalProps> = ({ isOpen, onClose, noEnoughSeats }) => {
  const organization = useAppSelector((state: RootState) => state.organization?.data)
  const organizationAdminProfile = useAppSelector(
    (state: RootState) => state.organizationAdmin.profile
  )
  const organizationId = organization?.id

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormInputs>({
    defaultValues: {
      email: '',
      isPrescriber: false,
      adminRole: null,
    },
  })

  const toast = useToast()
  const currentAdminRole = watch('adminRole')
  const isPrescriber = watch('isPrescriber')

  const isOwner = organizationAdminProfile?.isOwner
  const userInvitationNeedApproval = !isOwner && organization?.settings?.userInvitationNeedApproval

  const { trigger: submitUserInvitation, loading } = useApi(
    OrgMembershipApiClient.submitInvitation,
    {
      onSuccess: (res: any) => {
        handleClose()
        toast({
          title: 'User Invitation Sent Successfully',
          description: userInvitationNeedApproval
            ? 'The invitation has been sent to the organisation owner for approval.'
            : `An invitation email has been sent to ${res?.email}.`,
          status: 'success',
          isClosable: true,
          duration: 3000,
        })
      },
      onFailure: (error: AxiosError) => {
        const response = error.response as AxiosResponse<{ message: string }> | undefined
        toast({
          title: 'User Invitation Sent Failed',
          description: response?.data?.message || 'An error occurred while sending the invitation.',
          status: 'error',
          isClosable: true,
          duration: 3000,
        })
      },
    }
  )

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleAdminRoleChange = (newRole: AdminRole) => {
    setValue('adminRole', newRole === currentAdminRole ? null : newRole)
  }

  const handlePrescriberChange = (checked: boolean) => {
    setValue('isPrescriber', checked)
  }

  const onSubmit = async (data: InviteFormInputs) => {
    if ((!data.isPrescriber && !data.adminRole) || !organizationId) {
      return
    }

    const permissions = data.adminRole ? convertRoleToPermissions(data.adminRole) : []

    await submitUserInvitation({
      organizationId,
      email: data.email,
      isPrescriber: data.isPrescriber,
      permissions,
    })
  }

  const isAtLeastOneRoleSelected = isPrescriber || currentAdminRole !== null

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Invite New User</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack spacing={4}>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Invitee Email</FormLabel>
                <Input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  placeholder="Enter email address"
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!isAtLeastOneRoleSelected}>
                <FormLabel>Role</FormLabel>
                <VStack align="stretch" spacing={2} bgColor="gray.50" p={4} rounded="md">
                  <Controller
                    name="isPrescriber"
                    control={control}
                    render={({ field }) => (
                      <PrescriberRole
                        isChecked={field.value}
                        disabled={noEnoughSeats}
                        onChange={handlePrescriberChange}
                        noEnoughSeats={noEnoughSeats}
                      />
                    )}
                  />

                  {ADMIN_ROLES.map(({ role, label, description }) => (
                    <Controller
                      key={role}
                      name="adminRole"
                      control={control}
                      render={() => (
                        <AdminRoleOption
                          role={role}
                          label={label}
                          description={description}
                          isSelected={currentAdminRole === role}
                          onChange={handleAdminRoleChange}
                        />
                      )}
                    />
                  ))}
                </VStack>
                {!isAtLeastOneRoleSelected && (
                  <FormErrorMessage>Please select at least one role</FormErrorMessage>
                )}
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
              loadingText="Sending"
              isDisabled={!isAtLeastOneRoleSelected || loading}
            >
              Send Invitation
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
